import { useCallback, useRef, useState } from 'react';
import {
  Camera,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  Loader2,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { QuotePhoto } from '../../types/admin';

// ── Image compression ─────────────────────────────────────────────────────────

const MAX_DIM = 1200;
const MAX_DATA_URL_LENGTH = 500_000; // ~500 KB

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

async function compressImage(file: File): Promise<string> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  let width = img.naturalWidth;
  let height = img.naturalHeight;
  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = MAX_DIM / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  let quality = 0.7;
  for (;;) {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_DATA_URL_LENGTH) return dataUrl;

    if (quality > 0.4) {
      quality = Math.max(0.4, quality - 0.1);
    } else {
      width = Math.round(width * 0.75);
      height = Math.round(height * 0.75);
      quality = 0.6;
      if (width < 100) return canvas.toDataURL('image/jpeg', 0.4);
    }
  }
}

// ── Delete Confirm Sheet ──────────────────────────────────────────────────────

function DeletePhotoSheet({
  onConfirm,
  onClose,
  deleting,
}: {
  onConfirm: () => void;
  onClose: () => void;
  deleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={deleting ? undefined : onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-h3 text-midnight">Delete Photo</h2>
          {!deleting && (
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <p className="text-[13px] text-slate leading-snug mb-5">
          Are you sure you want to delete this photo? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="flex-1 h-11 rounded-xl border border-stone text-[13px] font-medium text-midnight
                       hover:bg-mist active:bg-stone/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 h-11 rounded-xl bg-rose text-[13px] font-medium text-white
                       hover:bg-rose/90 active:opacity-80 transition-colors disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PhotosNotesPage() {
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading, error, updateQuote, refetch } = useSharedQuote();

  const [photosExpanded, setPhotosExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QuotePhoto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesValue, setNotesValue] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDraft = quote?.quoteStatus === 'draft';
  const photos = quote?.photos ?? [];
  const photoCount = photos.length;

  // Notes: local state tracks edits; initial value comes from quote
  const displayNotes = notesValue ?? quote?.notes ?? '';

  const handleNotesChange = useCallback((value: string) => {
    setNotesValue(value);
    // Auto-save with debounce
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      setNotesSaving(true);
      try {
        await updateQuote({ notes: value });
      } catch {
        // Silent fail — user can see saving indicator
      } finally {
        setNotesSaving(false);
      }
    }, 800);
  }, [updateQuote]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !orgId || !quote) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';

    setUploading(true);
    try {
      const imageData = await compressImage(file);
      await api.post(`/quotes/${orgId}/${quote.id}/photos`, {
        imageData,
        fileName: file.name,
      });
      await refetch();
      // Auto-expand photos section when first photo is added
      setPhotosExpanded(true);
    } catch (err) {
      console.error('[photo upload]', err);
    } finally {
      setUploading(false);
    }
  }

  async function handleDeletePhoto() {
    if (!deleteTarget || !orgId || !quote) return;
    setDeleting(true);
    try {
      await api.del(`/quotes/${orgId}/${quote.id}/photos/${deleteTarget.id}`);
      await refetch();
      setDeleteTarget(null);
    } catch (err) {
      console.error('[photo delete]', err);
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">{error ?? 'Quote not found'}</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-6 md:max-w-2xl">
      <h1 className="font-display text-h2 text-midnight mb-5">Photos &amp; Notes</h1>

      {!isDraft && <ReadOnlyBanner status={quote.quoteStatus} />}

      {/* ── Pictures card ────────────────────────────────────────── */}
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm mb-4 overflow-hidden">
        {/* Header — always visible */}
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => setPhotosExpanded(v => !v)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
          >
            <div className="shrink-0 w-8 h-8 rounded-lg bg-plum/10 flex items-center justify-center">
              <Camera size={15} className="text-plum" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-small text-midnight">Pictures</p>
              <p className="text-[11px] text-slate">
                {photoCount === 0
                  ? 'No photos added'
                  : `${photoCount} photo${photoCount === 1 ? '' : 's'}`}
              </p>
            </div>
            {photosExpanded
              ? <ChevronUp size={16} className="shrink-0 text-slate" />
              : <ChevronDown size={16} className="shrink-0 text-slate" />}
          </button>

          {/* Add photo button — always visible in header */}
          {isDraft && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || photoCount >= 20}
              className="shrink-0 w-9 h-9 rounded-lg border border-plum/30 bg-plum/5
                         flex items-center justify-center
                         hover:bg-plum/10 active:bg-plum/15 disabled:opacity-40
                         transition-colors"
              aria-label="Add photo"
            >
              {uploading
                ? <Loader2 size={14} className="text-plum animate-spin" />
                : <Camera size={16} className="text-plum" />}
            </button>
          )}
        </div>

        {/* Expanded content */}
        {photosExpanded && (
          <div className="border-t border-stone">
            {photoCount === 0 ? (
              <div className="px-4 py-8 flex flex-col items-center gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-mist flex items-center justify-center">
                  <ImageIcon size={22} className="text-slate" />
                </div>
                <p className="text-[13px] text-slate">
                  {isDraft
                    ? 'Tap the camera button to add site photos'
                    : 'No photos were added to this quote'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 md:grid-cols-4">
                {photos.map(photo => (
                  <div
                    key={photo.id}
                    className="relative group aspect-square rounded-lg overflow-hidden bg-mist border border-stone"
                  >
                    <img
                      src={photo.url}
                      alt={photo.fileName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {isDraft && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(photo)}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-midnight/60
                                   flex items-center justify-center
                                   opacity-0 group-hover:opacity-100 active:opacity-100
                                   transition-opacity"
                        aria-label={`Delete ${photo.fileName}`}
                      >
                        <Trash2 size={13} className="text-white" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isDraft && photoCount > 0 && photoCount < 20 && (
              <div className="px-3 pb-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-10 rounded-lg border border-dashed border-plum/30 bg-plum/5
                             flex items-center justify-center gap-2 text-[13px] text-plum font-medium
                             hover:bg-plum/10 active:bg-plum/15 disabled:opacity-40
                             transition-colors"
                >
                  {uploading
                    ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                    : <><Camera size={14} /> Add Photo</>}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Notes card ───────────────────────────────────────────── */}
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stone">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-plum/10 flex items-center justify-center">
            <StickyNote size={15} className="text-plum" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-small text-midnight">Notes</p>
          </div>
          {notesSaving && (
            <span className="text-[11px] text-slate italic shrink-0">Saving…</span>
          )}
        </div>

        <div className={`px-4 py-3 ${!isDraft ? 'pointer-events-none opacity-60 select-none' : ''}`}>
          <textarea
            value={displayNotes}
            onChange={e => handleNotesChange(e.target.value)}
            placeholder={isDraft
              ? 'Add notes about this quote — site conditions, special requirements, client preferences…'
              : 'No notes added'}
            rows={8}
            className="w-full min-h-[200px] resize-y rounded-lg border border-stone bg-white
                       px-3 py-2.5 text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150
                       md:min-h-[160px]"
            readOnly={!isDraft}
          />
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Delete confirmation sheet */}
      {deleteTarget && (
        <DeletePhotoSheet
          deleting={deleting}
          onConfirm={handleDeletePhoto}
          onClose={() => { if (!deleting) setDeleteTarget(null); }}
        />
      )}
    </div>
  );
}
